// Judge.me media records shared by the Red product review list and gallery.
(() => {
  const videoPlaceholder = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120"><rect width="120" height="120" fill="#242424"/></svg>');
  const posterRequests = new Map();
  const resolveVimeoPoster = id => {
    id = String(id);
    if (!/^\d+$/.test(id)) return Promise.resolve(null);
    if (posterRequests.has(id)) return posterRequests.get(id);
    const request = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${id}`, {signal: controller.signal});
        if (!response.ok) return null;
        const data = await response.json();
        const url = new URL(data.thumbnail_url);
        return url.protocol === 'https:' ? url.href : null;
      } catch { return null; }
      finally { clearTimeout(timeout); }
    })();
    posterRequests.set(id, request);
    return request;
  };
  const video = record => {
    if (!record || typeof record !== 'object') return null;
    let provider, id;
    if (record.hosting_vendor === 'vimeo') {
      provider = 'vimeo'; id = String(record.external_id ?? record.id ?? '');
      if (!/^\d+$/.test(id)) return null;
    } else if (record.media_platform_name === 'yt') {
      provider = 'youtube'; id = String(record.media_platform_url ?? record.id ?? '');
      if (!/^[\w-]{11}$/.test(id)) return null;
    } else {
      if (record.hosting_vendor && record.hosting_vendor !== 'cloudflare') return null;
      provider = 'cloudflare'; id = String(record.external_id ?? record.id ?? '');
      if (!/^[a-f\d]{32}$/i.test(id)) return null;
    }
    const src = provider === 'vimeo' ? `https://player.vimeo.com/video/${id}` :
      provider === 'youtube' ? `https://www.youtube-nocookie.com/embed/${id}` :
      `https://iframe.videodelivery.net/${id}`;
    const thumbnail = provider === 'vimeo' ? videoPlaceholder :
      provider === 'youtube' ? `https://img.youtube.com/vi/${id}/0.jpg` :
      `https://videodelivery.net/${id}/thumbnails/thumbnail.jpg?time=1s&height=320`;
    return { type: 'video', provider, id, src, thumbnail };
  };
  const normalize = (review = {}) => {
    const media = [];
    const pictures = Array.isArray(review.pictures_urls) ? review.pictures_urls :
      Array.isArray(review.pictures) ? review.pictures : [];
    for (const picture of pictures) {
      const source = typeof picture === 'string' ? picture :
        picture?.original || picture?.huge || picture?.compact || picture?.small;
      if (!source) continue;
      try {
        const url = new URL(source, typeof location === 'object' ? location.href : 'https://lohriz.com/');
        if (url.protocol !== 'https:' && url.protocol !== 'http:') continue;
        const thumbnail = new URL(url.href);
        // Match the 139px review-card slot while keeping the zoom URL unchanged.
        if (thumbnail.hostname === 'review-images.judgeme.com') thumbnail.searchParams.set('width', '360');
        media.push({ type: 'image', src: url.href, thumbnail: thumbnail.href });
      } catch {}
    }
    for (const id of Array.isArray(review.video_external_ids) ? review.video_external_ids : []) {
      if (typeof id !== 'string' && typeof id !== 'number') continue;
      const item = video({ hosting_vendor: String(id).length > 16 ? 'cloudflare' : 'vimeo', external_id: id });
      if (item) media.push(item);
    }
    for (const record of Array.isArray(review.media_platform_hosted_video_infos) ? review.media_platform_hosted_video_infos : []) {
      const item = video(record);
      if (item) media.push(item);
    }
    const seen = new Set();
    return media.filter(item => !seen.has(item.src) && seen.add(item.src));
  };
  const hydrated = new WeakMap();
  const hydratePosters = (scope = document) => {
    scope.querySelectorAll('[data-review-media-type="video"]').forEach(element => {
      const source = element.dataset.imageSrc || element.getAttribute('href');
      const id = source?.match(/^https:\/\/player\.vimeo\.com\/video\/(\d+)(?:\?|$)/)?.[1];
      const image = element.querySelector('img');
      if (!id || !image || hydrated.get(image) === source) return;
      hydrated.set(image, source);
      resolveVimeoPoster(id).then(poster => {
        if (!poster || !image.isConnected || hydrated.get(image) !== source ||
          (element.dataset.imageSrc || element.getAttribute('href')) !== source) return;
        element.dataset.mediaThumbnail = poster;
        image.style.visibility = '';
        image.src = poster;
      });
    });
  };
  const initials = value => {
    const words = (String(value || '').trim() || 'Customer').split(/\s+/);
    return [words[0], words.length > 1 ? words[words.length - 1] : ''].map(word => Array.from(word)[0] || '').join('').toLocaleUpperCase();
  };
  const avatarUrl = value => {
    if (typeof value !== 'string') return '';
    try { const url = new URL(value); return ['https:', 'http:'].includes(url.protocol) ? url.href : ''; }
    catch { return ''; }
  };
  const avatarTargets = new WeakSet();
  const mountAvatar = (element, urls = {}) => {
    // Judge.me can explicitly return null for reviewers without an avatar.
    const normal = avatarUrl(urls?.normal);
    if (!normal || avatarTargets.has(element)) return;
    avatarTargets.add(element);
    const image = document.createElement('img');
    image.alt = ''; image.decoding = 'async';
    image.style.cssText = 'display:block;width:100%;height:100%;object-fit:cover;border-radius:inherit';
    image.addEventListener('load', () => element.replaceChildren(image), {once:true});
    image.addEventListener('error', () => image.remove(), {once:true});
    const retina = avatarUrl(urls.retina);
    if (retina) image.srcset = `${normal} 1x, ${retina} 2x`;
    image.src = normal;
  };
  const hydrateAvatars = (scope = document) => scope.querySelectorAll('[data-review-avatar-normal]').forEach(element => mountAvatar(element, {normal:element.dataset.reviewAvatarNormal, retina:element.dataset.reviewAvatarRetina}));
  const api = { normalize, video, resolveVimeoPoster, hydratePosters, initials, avatarUrl, mountAvatar, hydrateAvatars };
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (typeof window === 'object') window.rhpReviewMedia = api;
  if (typeof document === 'object') {
    const start = () => {
      hydratePosters();
      hydrateAvatars();
      new MutationObserver(() => { hydratePosters(); hydrateAvatars(); }).observe(document.body, {childList:true, subtree:true});
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once:true});
    else start();
  }
})();
